import { useCallback, useRef } from 'react';

import { AutoScroller, Cursor, Feedback, PreventSelection } from '@dnd-kit/dom';
import { DragDropProvider } from '@dnd-kit/react';
import { Box } from '@/shared/ui';

import { useAppSelector } from '@/store/hooks';

import {
  selectCurrentMarketTabApps,
  selectCurrentTabId,
  selectDashboardName
} from '@/features/dashboard/state/dashboardEdit.selectors';
import { useDashboardEditActions } from '../../hooks/useDashboardEditActions';
import { BlankColumn, Column } from './components';
import { useDragDropHandlers } from './hooks';

import type { AppsOrderProps } from './types';
/**
 * AppsOrder component for managing apps and their tabs in dashboard edit mode.
 *
 * Features:
 * - Drag-and-drop reordering of apps
 * - Drag-and-drop reordering of tabs within apps
 * - Move tabs between apps via drag-and-drop
 * - Create new apps by dropping tabs into blank column
 * - Move apps/tabs to other dashboards via menu
 *
 * Architecture:
 * - Uses @dnd-kit/react for drag-and-drop
 * - State managed via Redux (dashboardEdit slice)
 * - Components extracted to ./components/
 * - Hooks extracted to ./hooks/
 */
export const AppsOrder = ({ onTabCreated, preview }: AppsOrderProps) => {
  const currentTabId = useAppSelector(selectCurrentTabId);
  const marketTabApps = useAppSelector(selectCurrentMarketTabApps);
  const dashboardName = useAppSelector(selectDashboardName);
  const actions = useDashboardEditActions();

  const container = useRef<HTMLDivElement | null>(null);
  const shouldShowBlankColumn = marketTabApps.some(app => app.tabs.length > 1);

  const { isDragging, handleDragStart, handleDragEnd, handleDragOver } = useDragDropHandlers({
    currentTabId,
    marketTabApps
  });

  const handleRemoveApp = useCallback(
    (appId: string) => {
      if (!currentTabId) return;
      actions.removeApp(currentTabId, appId);
    },
    [currentTabId, actions]
  );

  const handleRemoveItem = useCallback(
    (appId: string, itemId: string) => {
      if (!currentTabId) return;
      actions.removeTabFromApp(currentTabId, appId, itemId);
    },
    [currentTabId, actions]
  );

  return (
    <DragDropProvider
      plugins={[Feedback, AutoScroller, Cursor, PreventSelection]}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
    >
      <Box
        ref={container}
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: '1fr',
            lg: `repeat(2, 1fr)`
          }
        }}
      >
        {marketTabApps.map((app, index) => (
          <Column
            key={app.id}
            app={app}
            container={container}
            currentDashboardName={dashboardName}
            currentTabId={currentTabId || ''}
            id={app.id}
            index={index}
            isDragging={isDragging}
            items={app.tabs}
            preview={preview}
            onRemoveApp={handleRemoveApp}
            onRemoveItem={itemId => {
              handleRemoveItem(app.id, itemId);
            }}
            onTabCreated={onTabCreated}
          />
        ))}
        {shouldShowBlankColumn && <BlankColumn />}
      </Box>
    </DragDropProvider>
  );
};

export default AppsOrder;
